import CustomFields from '../collections/CustomFields';
import CustomField from '../models/CustomField';
import { baseApi } from 'core/api';
import { useOrganizationStore } from 'store/modules';

const state = () => ({
    _items: null,
    _customFieldsItems: [],
    _types: null,
    _pagination: {},
    _parameters: {
        page: 1,
        sortOrder: 'desc',
        sortBy: 'created_at',
        search: null,
        length: 10,
    },
    _item: null,
    _editShow: null,
    _deleteShow: null,
    _loading: false,
    _loadingByContact: false,
});

const getters = {
    customFields: (state) => state._items,
    customFieldsItems: (state) => state._customFieldsItems,
    customFieldsItemsTemplate: (state) => {
        return state._customFieldsItems.map((item) => ({
            template: `{ ${item.key} }`,
            label: item.name,
        }));
    },
    customFieldsParameters: (state) => state._parameters,
    customFieldsPagination: (state) => state._pagination,
    customFieldsTypes: (state) => state._types,
    customFieldsTypesArray: (state) => {
        const array = [];

        for (let key in state._types) {
            array.push({
                key: key,
                value: state._types[key],
            });
        }

        return array;
    },
    customFieldsEditShow: (state) => state._editShow,
    customFieldsDeleteShow: (state) => state._deleteShow,
    customFieldsItem: (state) => state._item,
};

const actions = {
    async customFieldsSearch(params = {}) {
        // customFieldsSearch
        if (this._loading) return;

        this._setCustomFieldsLoading(true);

        params = {
            ...params,
            all: 1,
        };
        let response = null;

        try {
            await this.setCustomFieldsTypes();

            response = await baseApi.get('core/custom-fields/fields', { params });

            let items = new CustomFields(response.data, {
                types: this._types,
            });

            this._setCustomFieldsItemsSearch(items.models);

            return items.models;
        } catch (e) {
            return e;
        } finally {
            this._setCustomFieldsLoading(false);
        }
    },

    async customFieldsLoad(data = {}) {
        // customFieldsLoad
        if (this._loading) return;

        this._setCustomFieldsLoading(true);

        const retry = Boolean(data.retry);
        let response = null;

        if (retry === true) {
            this._setCustomFieldsRetryLoading();
        }

        try {
            await this.setCustomFieldsTypes();

            response = await baseApi.get('core/custom-fields/fields', {
                params: this._parameters,
            });

            this._setCustomFieldsLoaded(response.data);

            return response;
        } catch (e) {
            this._setCustomFieldsErrorLoading();

            return e;
        } finally {
            this._setCustomFieldsLoading(false);
        }
    },

    setCustomFieldsTypes() {
        // customFieldsTypes
        if (this._types) return;

        return baseApi.get('core/custom-fields/types').then((response) => {
            this._setCustomFieldsTypes(response.data);

            return response;
        });
    },

    customFieldsReset() {
        this._setCustomFieldsReset();
    },

    customFieldsUpdateSearch(search) {
        this._setCustomFieldsUpdateSearch(search);
        this.customFieldsLoad();
    },

    customFieldsUpdatePage(page) {
        this._setCustomFieldsUpdatePage(page);
        this.customFieldsLoad();
    },

    customFieldsUpdateLength(page) {
        this._setCustomFieldsUpdateLength(page);
        this.customFieldsLoad();
    },

    customFieldsClearSearch() {
        this._setCustomFieldsSearchCleared();
        this.customFieldsLoad();
    },

    deleteCustomFieldsFieldById({ id, form }) {
        return form.delete(`core/custom-fields/field/${id}`).then((response) => {
            this.customFieldRemove(response.data.data);
            useOrganizationStore()._setOrganizationUpdateCustomFields({
                type: 'delete',
                data: response.data.data,
            });

            return response;
        });
    },

    updateCustomFieldsFieldById({ id, form }) {
        return form.put(`core/custom-fields/field/${id}`).then((response) => {
            this.customFieldUpdate(response.data.data);
            useOrganizationStore()._setOrganizationUpdateCustomFields({
                type: 'edit',
                data: response.data.data,
            });

            return response;
        });
    },

    createCustomFieldsField(form) {
        return form.post('core/custom-fields/field').then((response) => {
            this.customFieldAdd(response.data.data);
            useOrganizationStore()._setOrganizationUpdateCustomFields({
                type: 'create',
                data: response.data.data,
            });

            return response.data.data;
        });
    },

    customFieldsUpdateSort(sort) {
        this._setCustomFieldsUpdateSort(sort);
        this.customFieldsLoad();
    },

    customFieldRemove(data) {
        this._setCustomFieldsDelete(data);
    },

    customFieldUpdate(data) {
        this._setCustomFieldsEdit(data);
    },

    customFieldAdd(data) {
        this._setCustomFieldsCreate(data);
    },

    customFieldEditShow(data) {
        this._setCustomFieldsEditShow(data);
    },

    customFieldDeleteShow(data) {
        this._setCustomFieldsDeleteShow(data);
    },

    async getContactsCustomFieldsById(contact) {
        if (this._loadingByContact) return;

        this._setCustomFieldsByContactLoading(true);

        try {
            await this.setCustomFieldsTypes();
            const response = await baseApi.get(`core/contacts/custom-fields/${contact.id}`);

            return response.data.data;
        } catch (e) {
            return [];
        } finally {
            this._setCustomFieldsByContactLoading(false);
        }
    },

    // Private methods for all operations
    _setCustomFieldsByContactLoading(status) {
        this._loadingByContact = status;
    },

    _setCustomFieldsLoading(status) {
        this._loading = status;
    },

    _setCustomFieldsItemsSearch(items) {
        this._customFieldsItems = items;
    },

    _setCustomFieldsEditShow({ status, item }) {
        this._editShow = status;

        if (status) {
            this._item = item;
        } else {
            this._item = null;
        }
    },

    _setCustomFieldsDeleteShow({ status, item }) {
        this._deleteShow = status;

        if (status) {
            this._item = item;
        } else {
            this._item = null;
        }
    },

    _setCustomFieldsTypes({ data }) {
        this._types = data.types;
    },

    _setCustomFieldsReset() {
        this._items = null;
        this._parameters = {
            page: 1,
            sortOrder: 'desc',
            sortBy: 'created_at',
            search: null,
            length: 10,
        };
    },

    _setCustomFieldsLoaded(data) {
        const contacts = new CustomFields(data.data, {
            types: this._types,
        });

        this._items = contacts.models;
        this._pagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            more: data.next_page_url,
        };
    },

    _setCustomFieldsRetryLoading() {
        this._items = null;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setCustomFieldsErrorLoading() {
        this._items = false;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setCustomFieldsUpdateSearch(data) {
        this._parameters.page = 1;
        this._parameters.search = data;
    },

    _setCustomFieldsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setCustomFieldsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setCustomFieldsSearchCleared() {
        this._parameters.search = null;
    },

    _setCustomFieldsEdit(data) {
        if (!this._items) return;

        this._items = this._items.map((item) => {
            if (item.id === data.id) {
                data.types = this._types;
                item = new CustomField(data);
            }

            return item;
        });
    },

    _setCustomFieldsDelete(data) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.filter((o) => o.id !== data.id);
        --this._pagination.total;

        if (this._items.length < this._parameters.length) {
            this._pagination.to = this._items.length;
        }
    },

    _setCustomFieldsCreate(data) {
        data.types = this._types;

        const item = new CustomField(data);

        if (this._items === null) {
            this._items = [item];
        } else {
            const existField = this._items.find((item) => item.key === data.key);

            if (existField) {
                return;
            }

            const indexR = this._items
                .slice()
                .reverse()
                .findIndex((item) => item.editable === false);
            const index = this._items.length - indexR;

            this._items.splice(index, 0, item);

            if (this._items.length > this._parameters.length) {
                this._items.pop();
            } else {
                this._pagination.to = this._items.length;
            }

            if (this._pagination.from === null) {
                this._pagination.from = 1;
            }

            ++this._pagination.total;
        }
    },

    _setCustomFieldsUpdateSort(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
    },
};

export default {
    state,
    getters,
    actions,
};
